import type * as React from 'react'

import { cn } from '../../lib/utils'

/**
 * A square rotor: a 2px stroke in the current colour with its end side open,
 * turning once every 0.9s. No dots, no pulse ring, no rounded arc. Inside a
 * button the signature layer's progress stroke replaces it (DESIGN.md
 * "Buttons: Busy"); this is for a region that is loading on its own.
 *
 * It is `aria-hidden` unless given a label, because the common case is a
 * spinner inside a control that already announces itself — a Button with
 * `aria-busy`. A second announcement there is noise. When the spinner is the
 * only feedback on screen, pass `label` and it becomes a live status region.
 */
function Spinner({
  className,
  size = 'md',
  label,
  ...props
}: React.ComponentProps<'span'> & { size?: 'sm' | 'md'; label?: string }) {
  const rotor = cn(
    'inline-block shrink-0 animate-spin rounded-[1px] border-2 border-current border-e-transparent [animation-duration:0.9s]',
    size === 'sm' ? 'size-3' : 'size-3.5',
    className,
  )

  // Two returns rather than one element with a conditional `role`. `aria-label`
  // is only valid on a span once that span has a role, and expressing that as
  // `role={label ? 'status' : undefined}` hides the relationship from the a11y
  // lint — which then reads the decorative case as a mislabelled span.
  if (label) {
    return (
      <span data-slot="spinner" role="status" aria-label={label} className={rotor} {...props} />
    )
  }

  return <span data-slot="spinner" aria-hidden="true" className={rotor} {...props} />
}

export { Spinner }
